import { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { addChatThread } from "../../socket";
import { ChatClient } from "@azure/communication-chat";
import { AzureCommunicationTokenCredential } from "@azure/communication-common";
import { ArrowLeft, Smile, SendHorizontal, CornerUpLeft } from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import axios from "axios";
import crossIcon from '../../new_assets/cross.svg';
import "./ACSChat.css";
let from = "";

const ACSChat = forwardRef(
    (
        {
            roomId,
            userName,
            userId,
            token,
            ownerToken,
            clientObjectArr,
            CbFromParticipant,
            CallBackFunctionForGetUnreadMessages,
            isDarkTheme,
        },
        ref,
    ) => {
        const [isOpen, setIsOpen] = useState(false);
        const [message, setMessage] = useState("");
        const [messages, setMessages] = useState([]);
        const [chatClient, setChatClient] = useState(null);
        const [chatThreadClient, setChatThreadClient] = useState(null);
        const [users, setUsers] = useState([]);
        const [activeTab, setActiveTab] = useState("group-chat");
        const [selectedUser, setSelectedUser] = useState(null);
        const [privateChats, setPrivateChats] = useState({});
        const [showEmojiPicker, setShowEmojiPicker] = useState(false);
        const [totalUnreadCount, setTotalUnreadCount] = useState(0);
        const [unreadGroupMessages, setUnreadGroupMessages] = useState(0);
        const [readGroupMessages, setReadGroupMessages] = useState({});
        const [unreadDirectMessages, setUnreadDirectMessages] = useState(0);
        const [userUnreadCounts, setUserUnreadCounts] = useState({});
        const [lastMessages, setLastMessages] = useState({});
        const [readMessages, setReadMessages] = useState({});
        const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, messageId: null });
        const [replyingTo, setReplyingTo] = useState(null);
        const messagesEndRef = useRef(null);
        const isInitialized = useRef(false);
        const emojiPickerRef = useRef(null);
        const textareaRef = useRef(null);
        let chatClientInstance;
        let eventHandler;

        // Create a consistent conversation ID for any two users
        const getConversationId = (user1Id, user2Id) => {
            return [user1Id, user2Id].sort().join("-")
        }

        useEffect(() => {
            CallBackFunctionForGetUnreadMessages(totalUnreadCount)
        }, [totalUnreadCount])

        useImperativeHandle(ref, () => ({
            open: (fromAppJs = "chat") => {
                from = fromAppJs
                CbFromParticipant({ isOpen: true, from: "chat" })
                setIsOpen(true)
            },
            close: () => {
                CbFromParticipant({ isOpen: false, from: "chat" })
                setIsOpen(false)
            },
        }))

        useEffect(() => {
            // if (textareaRef.current) {
            //     textareaRef.current.style.height = "inherit"
            //     const scrollHeight = textareaRef.current.scrollHeight
            //     textareaRef.current.style.height = `${Math.min(scrollHeight, 120)}px`
            // }
        }, [message])

        // Handle clicks outside context menu
        useEffect(() => {
            const handleClickOutside = (event) => {
                if (contextMenu.visible) {
                    setContextMenu({ visible: false, x: 0, y: 0, messageId: null })
                }
            }

            document.addEventListener("click", handleClickOutside)
            return () => {
                document.removeEventListener("click", handleClickOutside)
            }
        }, [contextMenu])

        const handleContextMenu = (e, messageId, messageContent, senderName) => {
            e.preventDefault()
            setContextMenu({
                visible: true,
                x: e.clientX,
                y: e.clientY,
                messageId,
                messageContent,
                senderName,
            })
        }

        const handleReply = () => {
            if (contextMenu.messageId) {
                setReplyingTo({
                    id: contextMenu.messageId,
                    content: contextMenu.messageContent,
                    sender: contextMenu.senderName,
                })
                setContextMenu({ visible: false, x: 0, y: 0, messageId: null })
                // Focus the textarea after setting up the reply
                if (textareaRef.current) {
                    textareaRef.current.focus()
                }
            }
        }

        const cancelReply = () => {
            setReplyingTo(null)
        }

        const markMessagesAsRead = async (userId) => {
            if (!userId) return

            // Get conversation ID for this pair
            const conversationId = getConversationId(userId, selectedUser)

            const chatMessages = privateChats[conversationId] || []
            const unreadMessages = chatMessages.filter(
                (msg) => msg.sender?.communicationUserId !== userId && !readMessages[msg.id],
            )

            const messageIds = unreadMessages.map((msg) => msg.id)

            if (messageIds.length > 0) {
                // Update local read status
                setReadMessages((prev) => {
                    const updated = { ...prev }
                    messageIds.forEach((id) => {
                        updated[id] = true
                    })
                    return updated
                })

                // Send read receipt notification to the sender
                try {
                    if (chatThreadClient) {
                        // For each unread message, send a read receipt notification
                        for (const msg of unreadMessages) {
                            const senderId = msg.sender?.communicationUserId

                            // Send a special message that indicates a read receipt
                            await chatThreadClient.sendMessage(
                                {
                                    content: "READ_RECEIPT",
                                    metadata: {
                                        messageType: "readReceipt",
                                        originalMessageId: msg.id,
                                        recipientId: senderId,
                                    },
                                },
                                {
                                    senderDisplayName: userName,
                                },
                            )
                        }
                    }
                } catch (error) {
                    console.error("Failed to send read receipt:", error)
                }
            }
        }

        // Call this when entering a private chat or when new messages arrive
        useEffect(() => {
            if (selectedUser && activeTab === "private-chat") {
                markMessagesAsRead(userId)
            }
        }, [selectedUser, activeTab, privateChats])

        async function validateToken() {
            try {
                console.log("🔍 Validating token...")
                const tokenCredential = new AzureCommunicationTokenCredential(token)
                const chatClient = new ChatClient(
                    "https://odrvideocallcommunicationservicedev.india.communication.azure.com/",
                    tokenCredential,
                )

                // Attempt to list chat threads (if token is invalid, it will fail)
                const iterator = chatClient.listChatThreads()
                const firstThread = await iterator.next()

                console.log("✅ Token is valid! User can access chat.")
                return true
            } catch (error) {
                console.error("❌ Token validation failed:", error.message)
                return false
            }
        }

        const findName = (userId) => {
            const user = clientObjectArr.find((user) => user.userId === userId)
            return user ? user.displayName : "Unknown User"
        }

        const fetchParticipants = async (chatThreadClient) => {
            try {
                console.log("📋 Fetching participants from thread...")

                if (!chatThreadClient) {
                    console.error("Thread client is not initialized")
                    return
                }

                const participantsIterator = chatThreadClient.listParticipants()
                const participants = []

                console.log("👥 Fetching participants...", participantsIterator)

                // Iterate through all participants
                for await (const participant of participantsIterator) {
                    participants.push({
                        id: participant.id.communicationUserId,
                        displayName: participant.displayName || findName(participant.id.communicationUserId),
                    })
                }

                console.log("👥 Participants loaded:", participants)
                setUsers(participants)
            } catch (error) {
                console.error("❌ Error fetching participants:", error)
            }
        }

        const initializeChat = async () => {
            try {
                console.log("📢 Initializing Chat...")

                const isValid = await validateToken()
                if (!isValid) return

                const chatClientInstance = new ChatClient(
                    "https://odrvideocallcommunicationservicedev.india.communication.azure.com/",
                    new AzureCommunicationTokenCredential(token),
                )

                setChatClient(chatClientInstance)

                // Get or create the chat thread
                const threadResponse = await axios.post(`${addChatThread()}create-chat-thread`, {
                    roomId,
                    token,
                    userId,
                    userName,
                    ownerToken,
                })
                const threadId = threadResponse.data.data.threadId

                // 💡 Call the API to add the user to the chat thread
                await axios.post(`${addChatThread()}add-user-to-thread`, {
                    threadId,
                    token,
                    userId,
                    userName,
                    ownerToken,
                    roomId,
                })

                const threadClient = chatClientInstance.getChatThreadClient(threadId)
                setChatThreadClient(threadClient)

                chatClientInstance.startRealtimeNotifications()

                // Define event handler to handle new messages
                const eventHandler = (event) => {
                    console.log("📩 New Message Received:", event)

                    // Handle read receipts
                    if (event.metadata?.messageType === "readReceipt") {
                        const originalMessageId = event.metadata.originalMessageId

                        // Update the read status for this message
                        setReadMessages((prev) => ({
                            ...prev,
                            [originalMessageId]: true,
                        }))

                        // Don't process read receipts as regular messages
                        return
                    }

                    // Skip own messages for unread counts
                    const senderId = event.sender?.communicationUserId
                    const isOwnMessage = senderId === userId

                    if (!isOwnMessage) {
                        // Process the message whether it's private or public
                        if (event.metadata?.messageType === "directMessage") {
                            const recipientId = event.metadata.recipientId

                            // This is a direct message to the current user
                            if (recipientId === userId) {
                                // Increment total unread count if chat is closed (only for the recipient of private messages)
                                if (!isOpen) {
                                    setTotalUnreadCount((prev) => prev + 1)
                                }
                                
                                // Update unread direct messages count if not in private chat with this sender
                                // or if not currently viewing the participants list
                                if ((activeTab !== "private-chat" || selectedUser !== senderId) && activeTab !== "participants") {
                                    setUnreadDirectMessages((prev) => prev + 1)
                                }

                                // Always update individual user unread count, regardless of current tab
                                // This ensures we track which users have unread messages
                                setUserUnreadCounts((prev) => ({
                                    ...prev,
                                    [senderId]: (prev[senderId] || 0) + 1,
                                }))

                                // Store the last message for this user
                                const messageContent = event?.message || event?.content?.message || ""
                                setLastMessages((prev) => ({
                                    ...prev,
                                    [senderId]: {
                                        text: messageContent,
                                        timestamp: event.createdOn,
                                        sender: event.senderDisplayName,
                                    },
                                }))
                            }

                            // Create a conversation ID for this pair
                            const conversationId = getConversationId(senderId, recipientId)

                            // Add message to private chats using conversation ID
                            setPrivateChats((prev) => {
                                const existingMessages = prev[conversationId] || []

                                // Check for duplicates
                                const isDuplicate = existingMessages.some((msg) => msg.id === event.id)
                                if (isDuplicate) return prev

                                return {
                                    ...prev,
                                    [conversationId]: [...existingMessages, event],
                                }
                            })
                        } else {
                            // This is a group message
                            // Increment total unread count if chat is closed (only for group messages)
                            if (!isOpen) {
                                setTotalUnreadCount((prev) => prev + 1)
                            }
                            
                            // Update unread group messages count if not in group chat tab
                            // FIXED: Always increment unread group messages if not in group chat tab
                            // if (activeTab !== "group-chat") {
                            //     setUnreadGroupMessages((prev) => prev + 1)
                            // }
                            if (!isOpen || activeTab !== "group-chat") {
                                setUnreadGroupMessages((prev) => prev + 1)
                            }

                            // Add to group messages
                            setMessages((prev) => {
                                const isDuplicate = prev.some((msg) => msg.id === event.id)
                                return isDuplicate ? prev : [...prev, event]
                            })
                        }
                    } else {
                        // Handle own messages (no unread counts)
                        if (event.metadata?.messageType === "directMessage") {
                            const recipientId = event.metadata.recipientId

                            // Create a conversation ID for this pair
                            const conversationId = getConversationId(senderId, recipientId)

                            // Add to private chats using conversation ID
                            setPrivateChats((prev) => {
                                const existingMessages = prev[conversationId] || []

                                // Check for duplicates
                                const isDuplicate = existingMessages.some((msg) => msg.id === event.id)
                                if (isDuplicate) return prev

                                return {
                                    ...prev,
                                    [conversationId]: [...existingMessages, event],
                                }
                            })

                            // Store the last message for this recipient
                            const messageContent = event?.message || event?.content?.message || ""
                            setLastMessages((prev) => ({
                                ...prev,
                                [recipientId]: {
                                    text: messageContent,
                                    timestamp: event.createdOn,
                                    sender: "You",
                                },
                            }))
                        } else {
                            // Add to group messages
                            setMessages((prev) => {
                                const isDuplicate = prev.some((msg) => msg.id === event.id)
                                return isDuplicate ? prev : [...prev, event]
                            })
                        }
                    }
                }

                chatClientInstance.on("chatMessageReceived", eventHandler)
            } catch (error) {
                console.error("❌ Chat initialization failed:", error)
            }
        }

        const markGroupMessagesAsRead = () => {
            if (activeTab === "group-chat") {
                // Get IDs of unread messages
                const unreadMessageIds = messages
                    .filter(msg => !readGroupMessages[msg.id] && msg.sender?.communicationUserId !== userId)
                    .map(msg => msg.id)

                if (unreadMessageIds.length > 0) {
                    // Mark all as read
                    setReadGroupMessages(prev => {
                        const updated = { ...prev }
                        unreadMessageIds.forEach(id => {
                            updated[id] = true
                        })
                        return updated
                    })

                    // Reset unread count
                    setUnreadGroupMessages(0)
                }
            }
        }

        // Clear unread counts when opening chat
        useEffect(() => {
            if (isOpen) {
                setTotalUnreadCount(0)
            }
        }, [isOpen])

        useEffect(() => {
            if (activeTab === "group-chat" && isOpen) {
                markGroupMessagesAsRead()
            }
        }, [activeTab, messages, isOpen])

        useEffect(() => {
            if (activeTab === "group-chat") {
                markGroupMessagesAsRead()
            } else if (activeTab === "participants") {
                setUnreadDirectMessages(0)
            }
        }, [activeTab])

        useEffect(() => {
            if (isOpen) {
                setTotalUnreadCount(0)
                if (activeTab === "group-chat") {
                    markGroupMessagesAsRead()
                }
            }
        }, [isOpen])

        // Clear unread counts when switching tabs
        useEffect(() => {
            if (activeTab === "group-chat") {
                setUnreadGroupMessages(0);
            } else if (activeTab === "participants") {
                // When switching to participants tab, we should update the direct message counts
                // but not reset them completely since the user hasn't read the actual messages yet

                // We'll keep the individual user unread counts, but update the UI to show we're aware
                // of which tab the user is on
                setUnreadDirectMessages(0)
            }
        }, [activeTab])

        // Add a new useEffect to handle real-time updates of unread counts when viewing participants list
        useEffect(() => {
            // This effect runs when privateChats or activeTab changes
            // It ensures unread counts are updated in real-time

            if (activeTab === "participants") {
                // Recalculate total unread direct messages based on individual user counts
                const totalDirect = Object.values(userUnreadCounts).reduce((sum, count) => sum + count, 0)
                setUnreadDirectMessages(totalDirect)
            }
        }, [privateChats, activeTab, userUnreadCounts])

        // Clear unread counts for selected user
        useEffect(() => {
            if (selectedUser) {
                setUserUnreadCounts((prev) => ({
                    ...prev,
                    [selectedUser]: 0,
                }))

                // Recalculate total unread direct messages
                const totalDirect = Object.values(userUnreadCounts).reduce((sum, count) => sum + count, 0)
                setUnreadDirectMessages(totalDirect)
            }
        }, [selectedUser])

        useEffect(() => {
            if (!isInitialized.current) {
                isInitialized.current = true
                initializeChat()
            }

            return () => {
                if (chatClientInstance && eventHandler) {
                    chatClientInstance.off("chatMessageReceived", eventHandler)
                }
            }
        }, [])

        useEffect(() => {
            if (chatThreadClient) {
                fetchParticipants(chatThreadClient)
            }
        }, [chatThreadClient])

        // Auto-scroll to bottom when new messages arrive
        useEffect(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
        }, [messages, selectedUser, privateChats])

        const sendMessage = async (e) => {
            e?.preventDefault()
            if (!message.trim() || !chatThreadClient) return

            try {
                const uniqueMessageId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
                const messageMetadata = {}

                // If replying to a message, include the quoted message in metadata
                if (replyingTo) {
                    messageMetadata.quotedMessageId = replyingTo.id
                    messageMetadata.quotedMessage = replyingTo.content
                    messageMetadata.quotedSender = replyingTo.sender
                }

                if (activeTab === "group-chat" || !selectedUser) {
                    // Send group message
                    await chatThreadClient.sendMessage(
                        {
                            content: message,
                            id: uniqueMessageId,
                            metadata: {
                                messageType: "groupMessage",
                                ...messageMetadata,
                            },
                        },
                        {
                            senderDisplayName: userName,
                        },
                    )
                } else {
                    // Send private message
                    const recipient = clientObjectArr.find((user) => user.userId === selectedUser)
                    console.log("Recipient found:", recipient)
                    await chatThreadClient.sendMessage(
                        {
                            content: message,
                            id: uniqueMessageId,
                            metadata: {
                                messageType: "directMessage",
                                recipientId: selectedUser,
                                recipientName: recipient ? recipient.displayName : "Unknown User",
                                ...messageMetadata,
                            },
                        },
                        {
                            senderDisplayName: userName,
                        },
                    )
                }

                console.log("Message Sent with ID:", uniqueMessageId)
                setMessage("")
                setReplyingTo(null) // Clear the reply after sending
            } catch (error) {
                console.error("Full Error Details:", error)
                alert("Failed to send message. Please check your permissions.")
            }
        }

        const formatTime = (timestamp) => {
            if (!timestamp) return ""
            const date = new Date(timestamp)
            return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        }

        const startPrivateChat = (user) => {
            // Clear unread count for this specific user
            setUserUnreadCounts((prev) => ({
                ...prev,
                [user.userId]: 0,
            }))

            // Recalculate total unread direct messages
            setUserUnreadCounts((prev) => {
                const updatedCounts = {
                    ...prev,
                    [user.userId]: 0,
                }

                // Update the unread direct messages count
                const totalDirect = Object.values(updatedCounts).reduce((sum, count) => sum + count, 0)
                setUnreadDirectMessages(totalDirect)

                return updatedCounts
            })

            setSelectedUser(user.userId)
            setActiveTab("private-chat")
        }

        const backToParticipants = () => {
            setSelectedUser(null)
            setActiveTab("participants")
        }

        // Handle clicks outside emoji picker
        useEffect(() => {
            const handleClickOutside = (event) => {
                if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
                    // Check if the click was on the emoji button
                    const emojiButtons = document.querySelectorAll(".emoji-button")
                    let clickedOnEmojiButton = false

                    emojiButtons.forEach((button) => {
                        if (button.contains(event.target)) {
                            clickedOnEmojiButton = true
                        }
                    })

                    if (!clickedOnEmojiButton) {
                        setShowEmojiPicker(false)
                    }
                }
            }

            document.addEventListener("mousedown", handleClickOutside)
            return () => {
                document.removeEventListener("mousedown", handleClickOutside)
            }
        }, [])

        const handleEmojiClick = (emojiObject) => {
            setMessage((prev) => prev + emojiObject.emoji)
            setShowEmojiPicker(false)
        }

        const toggleEmojiPicker = (e) => {
            e.stopPropagation()
            setShowEmojiPicker(!showEmojiPicker)
        }

        // Sort participants by most recent message
        const getSortedParticipants = () => {
            return clientObjectArr
                .filter((user) => user.userId !== userId)
                .sort((a, b) => {
                    const lastMessageA = lastMessages[a.userId]
                    const lastMessageB = lastMessages[b.userId]

                    // If both have messages, sort by timestamp
                    if (lastMessageA && lastMessageB) {
                        return new Date(lastMessageB.timestamp) - new Date(lastMessageA.timestamp)
                    }

                    // If only one has a message, prioritize that one
                    if (lastMessageA) return -1
                    if (lastMessageB) return 1

                    // If neither has messages, keep original order
                    return 0
                })
        }

        const renderGroupChat = () => {
            return (
                <div className="chat-content">
                    <div className="messages-container">
                        {messages.map((msg, index) => {
                            const messageContent = msg?.message || msg?.content?.message || "[Message content unavailable]";
                            const senderName = msg.senderDisplayName || "Unknown";
                            const senderId = msg.sender?.communicationUserId;
                            const isOwnMessage = senderId === userId;

                            const user = clientObjectArr.find((u) => u.userId === senderId);
                            const userInitial = user ? user.displayName.charAt(0) : "?";

                            if (isOwnMessage) {
                                return (
                                    <div
                                        key={index}
                                        className={`group-message-item own-group-message`}
                                    >
                                        {/* Own message wrapped with avatar and content in same row */}
                                        <div
                                            className="own-message-wrapper"
                                            onContextMenu={(e) => handleContextMenu(e, msg.id, messageContent, senderName)}
                                        >
                                            <div className="message-content own-message-content">
                                                <div className="sender-name">You</div>
                                                {msg.metadata?.quotedMessage && (
                                                    <div className="quoted-message">
                                                        <CornerUpLeft size={16} className="reply-icon" />
                                                        <div className="quoted-content">
                                                            <div className="quoted-sender">{msg.metadata.quotedSender}</div>
                                                            <div className="quoted-text">{msg.metadata.quotedMessage}</div>
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="own-message-text">{messageContent}</div>
                                            </div>
                                            <div className="own-message-time">{formatTime(msg.createdOn)}</div>
                                            {/* <div className="own-message-avatar">
                                                <div className="own-avatar">{userInitial}</div>
                                            </div> */}
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <div>
                                    <div
                                        key={index}
                                        className="group-message-item"
                                    >
                                        {/* Other user message */}
                                        <div className="message-avatar">
                                            <div className="avatar-acs">{userInitial}</div>
                                        </div>
                                        <div
                                            className="message-content"
                                            onContextMenu={(e) => handleContextMenu(e, msg.id, messageContent, senderName)}
                                        >
                                            <div className="sender-name">{senderName}</div>
                                            {msg.metadata?.quotedMessage && (
                                                <div className="quoted-message">
                                                    <CornerUpLeft size={16} className="reply-icon" />
                                                    <div className="quoted-content">
                                                        <div className="quoted-sender">{msg.metadata.quotedSender}</div>
                                                        <div className="quoted-text">{msg.metadata.quotedMessage}</div>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="message-text" style={{color: "#282D38"}}>{messageContent}</div>

                                        </div>

                                    </div>
                                    <div className="message-time">{formatTime(msg.createdOn)}</div>

                                </div>

                            );
                        })}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Reply Preview & Input Section */}
                    <div className="message-input-container">
                        {replyingTo && (
                            <div className="reply-preview">
                                <div className="reply-content">
                                    <CornerUpLeft size={16} className="reply-icon" />
                                    <div>
                                        <div className="reply-sender">{replyingTo.sender}</div>
                                        <div className="reply-text">{replyingTo.content}</div>
                                    </div>
                                </div>
                                <button className="cancel-reply" onClick={cancelReply}>
                                    ×
                                </button>
                            </div>
                        )}
                        <textarea
                            ref={textareaRef}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    sendMessage();
                                }
                            }}
                            placeholder={replyingTo ? "Reply to message..." : "Type your message..."}
                            className="message-input"
                            rows="1"
                        />
                        <div className="message-actions">
                            {/* <button type="button" className="action-button emoji-button" onClick={toggleEmojiPicker}>
                                <Smile size={20} />
                            </button> */}
                            <button type="button" className="send-button" onClick={sendMessage}>
                                <SendHorizontal size={20} />
                            </button>
                        </div>
                        {showEmojiPicker && (
                            <div className="emoji-picker-container" ref={emojiPickerRef}>
                                <EmojiPicker onEmojiClick={handleEmojiClick} width={280} height={350} />
                            </div>
                        )}
                    </div>
                </div>
            );
        };


        const renderParticipantsList = () => {
            const sortedParticipants = getSortedParticipants()

            return (
                <div className="participants-list">
                    {sortedParticipants.map((user) => {
                        const hasUnread = userUnreadCounts[user.userId] > 0
                        const lastMessage = lastMessages[user.userId]
                        const messagePreview = lastMessage?.text
                            ? lastMessage.text.length > 20
                                ? lastMessage.text.slice(0, 20) + "..."
                                : lastMessage.text
                            : "Start the conversation now".slice(0, 36);

                        const messageTime = lastMessage ? formatTime(lastMessage.timestamp) : formatTime(new Date())

                        return (
                            <div key={user.userId} className="participant-item" onClick={() => startPrivateChat(user)}>
                                <div className="participant-avatar-acs">
                                    <div className="avatar-acs">{user.displayName.charAt(0)}</div>
                                </div>
                                <div className="participant-info">
                                    <div className="participant-details">
                                        <div className="participant-name">{user.displayName}</div>
                                        <div className="participant-time">{messageTime}</div>
                                    </div>
                                    <div className="participant-status">
                                        <span className="status-text">{messagePreview}</span>
                                        {hasUnread && <div className="unread-badge">{userUnreadCounts[user.userId]}</div>}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )
        }

        const renderPrivateChat = () => {
            if (!selectedUser) return null

            const chatPartner = clientObjectArr.find((user) => user.userId === selectedUser)

            // Get the conversation ID for this pair
            const conversationId = getConversationId(userId, selectedUser)

            // Retrieve only messages for this conversation
            const chatMessages = privateChats[conversationId] || []

            return (
                <div className="private-chat">
                    <div className="private-chat-header">
                        <button className="back-button" onClick={backToParticipants}>
                            <ArrowLeft size={20} />
                        </button>
                        <div className="chat-partner">
                            <div className="partner-avatar">
                                <div className="avatar-acs">{chatPartner?.displayName.charAt(0)}</div>
                            </div>
                            <div className="partner-info">
                                <div className={`partner-name${!isDarkTheme ? '-dark' : ''}`}>{chatPartner?.displayName}</div>
                                <div className="partner-status">Online</div>
                            </div>
                        </div>
                    </div>

                    <div className="private-messages-container">
                        {chatMessages.length === 0 && (
                            <div className="no-messages">
                                <p>No messages yet. Start the conversation!</p>
                            </div>
                        )}

                        {chatMessages.map((msg, index) => {
                            const messageContent = msg?.message || msg?.content?.message || "[Message content unavailable]"
                            const senderId = msg.sender?.communicationUserId
                            const isOwnMessage = senderId === userId
                            const senderName = isOwnMessage ? "You" : msg.senderDisplayName || "Unknown"

                            return (
                                <div
                                    key={index}
                                    className={`private-message ${isOwnMessage ? "own-message" : "other-message"}`}
                                    onContextMenu={(e) => handleContextMenu(e, msg.id, messageContent, senderName)}
                                >
                                    <div className="message-bubble">
                                        {msg.metadata?.quotedMessage && (
                                            <div className="quoted-message">
                                                <CornerUpLeft size={16} className="reply-icon" />
                                                <div className="quoted-content">
                                                    <div className="quoted-sender">{msg.metadata.quotedSender}</div>
                                                    <div className="quoted-text">{msg.metadata.quotedMessage}</div>
                                                </div>
                                            </div>
                                        )}
                                        <div className={`${isOwnMessage ? "private-own-message" : "private-other-message"}`} style={isOwnMessage ? { color: "#FFFFFF" } : { color: "#282D38" }}>{messageContent}</div>
                                        <div className={`message-time ${isOwnMessage ? "private-own-message" : "other-message"}`} style={isOwnMessage ? { color: "#FFFFFF" } : { color: "#282D38" }}>
                                            {formatTime(msg.createdOn)}
                                            {isOwnMessage && (
                                                <span className="read-receipt">
                                                    {readMessages[msg.id] ? (
                                                        // Double checkmark for read messages with #56E8A7 color
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="16"
                                                            height="16"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="#56E8A7"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        >
                                                            <path d="M18 6L9.7 16.4L6 13" />
                                                            <path d="M13 6L4.7 16.4L1 13" />
                                                        </svg>
                                                    ) : (
                                                        // Single checkmark for delivered but unread (keeping white)
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="16"
                                                            height="16"
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            stroke="white"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        >
                                                            <path d="M20 6L9 17L4 12" />
                                                        </svg>
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                        <div ref={messagesEndRef} />
                    </div>

                    <div className="message-input-container">
                        {replyingTo && (
                            <div className="reply-preview">
                                <div className="reply-content">
                                    <CornerUpLeft size={16} className="reply-icon" />
                                    <div>
                                        <div className="reply-sender">{replyingTo.sender}</div>
                                        <div className="reply-text">{replyingTo.content}</div>
                                    </div>
                                </div>
                                <button className="cancel-reply" onClick={cancelReply}>
                                    ×
                                </button>
                            </div>
                        )}
                        <textarea
                            ref={textareaRef}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault()
                                    sendMessage()
                                }
                            }}
                            placeholder={replyingTo ? "Reply to message..." : "Type your message..."}
                            className="message-input"
                            rows="1"
                        />
                        <div className="message-actions">
                            {/* <button type="button" className="action-button emoji-button" onClick={toggleEmojiPicker}>
                                <Smile size={20} />
                            </button> */}
                            <button type="button" className="send-button" onClick={sendMessage}>
                                <SendHorizontal size={20} />
                            </button>
                        </div>
                        {showEmojiPicker && (
                            <div className="emoji-picker-container" ref={emojiPickerRef}>
                                <EmojiPicker onEmojiClick={handleEmojiClick} width={280} height={350} />
                            </div>
                        )}
                    </div>
                </div>
            )
        }

        useEffect(() => {
            console.log("Unread Group Messages:", unreadGroupMessages);
        }, [unreadGroupMessages])

        return (
            <div className={`chat-container-${isOpen} ${isDarkTheme ? 'dark' : ''}`}>
                {isOpen && (
                    <div className="chat-sidebar">
                        <div className="chat-header">
                            <h2>Chat</h2>
                        </div>

                        <div className="chat-tabs">
                            <button
                                className={`tab-button ${activeTab === "group-chat" ? "active" : ""}`}
                                onClick={() => {
                                    setActiveTab("group-chat")
                                    setSelectedUser(null)
                                }}
                            >
                                Everyone
                                {unreadGroupMessages > 0 && <span className="tab-badge">{unreadGroupMessages}</span>}
                            </button>
                            <button
                                className={`tab-button ${activeTab === "participants" || activeTab === "private-chat" ? "active" : ""}`}
                                onClick={() => {
                                    setActiveTab("participants")
                                    setSelectedUser(null)
                                }}
                            >
                                One to One
                                {unreadDirectMessages > 0 && <span className="tab-badge">{unreadDirectMessages}</span>}
                            </button>
                        </div>

                        {activeTab === "group-chat" && renderGroupChat()}
                        {activeTab === "participants" && renderParticipantsList()}
                        {activeTab === "private-chat" && renderPrivateChat()}

                        <button
                            onClick={() => {
                                CallBackFunctionForGetUnreadMessages(0)
                                CbFromParticipant({ isOpen: false, from: "participant" })
                                setIsOpen(false)
                            }}
                            className="chat-close-button"
                        >
                            <img src={crossIcon} alt="close" />
                        </button>
                    </div>
                )}

                {/* Context Menu */}
                {contextMenu.visible && (
                    <div
                        className="context-menu"
                        style={{
                            position: "fixed",
                            top: contextMenu.y,
                            left: contextMenu.x,
                            zIndex: 1000,
                            // background: "#fff",
                            // boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                            borderRadius: "4px",
                            padding: "8px 0",
                        }}
                    >
                        <div
                            className="context-menu-item"
                            onClick={handleReply}
                            style={{
                                padding: "8px 16px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                background: !isDarkTheme ? "#282D38" : "#fff"
                            }}
                            
                        >
                            <CornerUpLeft size={16} />
                            <span>Reply</span>
                        </div>
                    </div>
                )}
            </div>
        )
    },
)

export default ACSChat;